import { useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X, Droplets, ArrowLeftRight } from 'lucide-react';
import PortalPopover from '../PortalPopover';
import { MATERIALS, MATERIAL_GROUP_ORDER, findMaterial } from '../../../data/materials';

// Per-row Material picker (Task #27 Phase D). Sichtbar nur wenn der
// Thermal-Mode für die Berechnung aktiv ist — sonst ist die Spalte gar
// nicht da (siehe MeasureTable). Pop-Over zeigt die Materialien
// gruppiert (Stahl / Alu / Kunststoff / GF-verstärkt / …) plus eine
// freie Suche darüber.
//
// Visuelle Marker neben dem Namen:
//   • Droplets-Icon   → hygroskopisch (PA-Typen): kann Feuchteaufnahme
//                        in der Größenordnung des thermischen Effekts
//                        haben, siehe Konzept-Doku § 2.1
//   • ArrowLeftRight  → anisotrop (GF-verstärkt): wir verwenden den
//                        Längswert in Faserrichtung; quer ist α höher

function fmtAlpha(alpha) {
  // α immer als „NN ·10⁻⁶/K" anzeigen — vertrautes Format für
  // Konstrukteure, leicht überfliegbar.
  return `${(alpha * 1e6).toFixed(1)} ·10⁻⁶/K`;
}

function tooltipFor(mat) {
  if (!mat) return 'Material wählen - ohne Material trägt das Maß nichts zur Wärmeausdehnung bei (α=0).';
  const parts = [`${mat.name}`, `α = ${fmtAlpha(mat.alpha)}`, `Quelle: ${mat.source}`];
  if (mat.hygroscopic) {
    parts.push('⚠ Hygroskopisch - Feuchteaufnahme kann thermischen Effekt erreichen oder übersteigen. Trocken konditionierte Werte einplanen oder Werkstoff prüfen.');
  }
  if (mat.anisotropic) {
    parts.push('ℹ α gilt in Faserrichtung (längs); quer zur Faserrichtung kann α 2–3× höher sein.');
  }
  return parts.join('\n');
}

// Gruppen-Reihenfolge aus der Material-Lib, gefiltert auf nur tatsächlich
// belegte Gruppen — wenn die Lib mal Gruppen rausnimmt, bleibt das UI
// konsistent ohne leere Headlines.
function buildGroups() {
  const byGroup = new Map();
  for (const mat of Object.values(MATERIALS)) {
    if (!byGroup.has(mat.group)) byGroup.set(mat.group, []);
    byGroup.get(mat.group).push(mat);
  }
  return MATERIAL_GROUP_ORDER
    .filter((g) => byGroup.has(g))
    .map((g) => ({ name: g, items: byGroup.get(g) }));
}

const GROUPS = buildGroups();
const ALL_FLAT = Object.values(MATERIALS).map((m) => ({
  ...m,
  searchKey: `${m.group} ${m.name}`.toLowerCase(),
}));

export default function MaterialCell({ value, onChange, readOnly = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  const mat = findMaterial(value);
  // Anzeige im Cell: bei gesetztem Material der kompakte Name (z.B.
  // "EN AW-6060"); bei leer ein subtiler Platzhalter. Kein full-name
  // weil die Zelle schmal ist und die Reihe sonst überläuft.
  const shortName = useMemo(() => {
    if (!mat) return 'Material';
    // „EN AW-6060 (AlMgSi)" → „EN AW-6060", „Baustahl S235JR (1.0038)" → „S235JR"
    return mat.name.split('(')[0].trim().replace(/^Baustahl |^Vergütungsstahl |^Wälzlagerstahl /, '');
  }, [mat]);

  const searchHits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return ALL_FLAT.filter((m) => m.searchKey.includes(q));
  }, [query]);

  const pick = (id) => {
    onChange(id);
    setOpen(false);
    setQuery('');
  };

  return (
    <>
      <button
        ref={ref}
        type="button"
        tabIndex={-1}
        disabled={readOnly}
        onClick={() => { if (readOnly) return; setOpen((o) => !o); setQuery(''); }}
        // readOnly tritt nur im Free-Read-only-Demo-Modus auf — Tooltip
        // sagt klar warum's nicht editierbar ist, statt einfach
        // grau-und-leise zu sein.
        title={readOnly
          ? `${tooltipFor(mat)}\n\nMaterial-Auswahl ist ein Pro-Feature.`
          : tooltipFor(mat)}
        className={`w-full text-left border border-line ${
          mat
            ? 'bg-brand-soft text-brand font-semibold'
            : 'bg-surface text-ink-3'
        } ${readOnly ? 'opacity-70' : ''}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '0 8px', height: 26,
          borderRadius: 5, cursor: readOnly ? 'not-allowed' : 'pointer',
          fontSize: 11,
        }}
      >
        <span className="truncate flex-1">{shortName}</span>
        {mat?.hygroscopic && <Droplets size={10} className="text-warn" aria-hidden="true" />}
        {mat?.anisotropic && <ArrowLeftRight size={10} className="text-ink-3" aria-hidden="true" />}
        <ChevronDown size={11} aria-hidden="true" />
      </button>

      <PortalPopover anchorRef={ref} open={open} onClose={() => setOpen(false)} minWidth={360}>
        <div className="bg-surface border border-line" style={{ borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          {/* Such-Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderBottom: '1px solid var(--color-line)' }}>
            <Search size={12} className="text-ink-3" aria-hidden="true" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Material suchen…"
              className="bg-transparent text-ink-2"
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: 12 }}
            />
            {value && (
              <button
                type="button"
                onClick={() => pick('')}
                title="Material entfernen (α=0)"
                className="text-ink-3 hover:text-err"
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11 }}
              >
                <X size={11} aria-hidden="true" /> löschen
              </button>
            )}
          </div>

          {/* Liste: entweder Such-Treffer flat, sonst gruppiert */}
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {searchHits ? (
              searchHits.length === 0 ? (
                <div className="text-ink-3" style={{ padding: '12px 14px', fontSize: 12 }}>
                  Keine Treffer für „{query}".
                </div>
              ) : (
                searchHits.map((m) => (
                  <MaterialRow key={m.id} mat={m} active={m.id === value} onPick={pick} />
                ))
              )
            ) : (
              GROUPS.map((g) => (
                <div key={g.name}>
                  <div
                    className="text-ink-3 bg-surface-2 border-b border-line"
                    style={{
                      padding: '4px 12px',
                      fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      position: 'sticky', top: 0, zIndex: 1,
                    }}
                  >
                    {g.name}
                  </div>
                  {g.items.map((m) => (
                    <MaterialRow key={m.id} mat={m} active={m.id === value} onPick={pick} />
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      </PortalPopover>
    </>
  );
}

function MaterialRow({ mat, active, onPick }) {
  return (
    <button
      type="button"
      onClick={() => onPick(mat.id)}
      title={tooltipFor(mat)}
      className={`w-full text-left hover:bg-brand-soft/40 border-b border-line ${
        active ? 'bg-brand-soft text-brand font-semibold' : 'text-ink-2'
      }`}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 12px',
        border: 'none', borderBottom: '1px solid var(--color-line)',
        background: active ? undefined : 'transparent',
        cursor: 'pointer', fontSize: 12,
      }}
    >
      <span className="flex-1 truncate">{mat.name}</span>
      {mat.hygroscopic && <Droplets size={11} className="text-warn" aria-hidden="true" title="hygroskopisch" />}
      {mat.anisotropic && <ArrowLeftRight size={11} className="text-ink-3" aria-hidden="true" title="anisotrop (Längs-α)" />}
      <span className="num text-ink-3" style={{ fontSize: 10.5, minWidth: 90, textAlign: 'right' }}>
        {fmtAlpha(mat.alpha)}
      </span>
    </button>
  );
}
